/**
 * 로그 API — 감사 로그 / 실패 로그
 */

import apiClient from './client';
import type { WorkflowRunLog } from './workflows';

// ── 감사 로그 ─────────────────────────────────────────────────────────────────

export type AuditAction =
  | 'login' | 'logout'
  | 'workflow.create' | 'workflow.update' | 'workflow.deploy' | 'workflow.delete'
  | 'user.create' | 'user.update' | 'user.delete'
  | 'kb.sync' | 'kb.create' | 'kb.delete'
  | 'system.settings';

export interface AuditLog {
  logId: string;
  userId: string;
  userEmail: string;
  action: AuditAction | string;
  target?: string;
  targetId?: string;
  result: 'success' | 'failure';
  ip?: string;
  errorMessage?: string;
  tenantId?: string;
  details?: {
    before?: Record<string, string>;
    after?: Record<string, string>;
  };
  createdAt: string;
}

// ── 실패 요약 (배너용) ─────────────────────────────────────────────────────────

export interface FailureSummary {
  count: number;
  recentNames: string[]; // 최근 실패한 워크플로우 이름 (최대 5개, 중복 제거)
}

// ── 토큰 사용량 ─────────────────────────────────────────────────────────────

export interface TokenUsageLog {
  logId: string;
  userId: string;
  userEmail?: string;
  tenantId?: string;
  workflowId?: string;
  workflowName?: string;
  chatSessionId?: string;
  chatId?: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
  costKrw: number;
  question?: string;
  answer?: string;
  createdAt: string;
}

// ── API ──────────────────────────────────────────────────────────────────────

export const logsApi = {
  /** 실패 워크플로우 실행 로그 목록 */
  async getFailures(params?: { limit?: number; workflowId?: string; tenantId?: string }): Promise<WorkflowRunLog[]> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.workflowId) query.set('workflowId', params.workflowId);
    if (params?.tenantId) query.set('tenantId', params.tenantId);
    const qs = query.toString() ? `?${query.toString()}` : '';
    const res = await apiClient.get<{ success: boolean; data: WorkflowRunLog[] }>(
      `/api/logs/failures${qs}`
    );
    return res.data.data;
  },

  /** 최근 24시간 실패 요약 (대시보드 배너용) */
  async getFailuresSummary(): Promise<FailureSummary> {
    const res = await apiClient.get<{ success: boolean; data: FailureSummary }>(
      '/api/logs/failures/summary'
    );
    return res.data.data;
  },

  /** 감사 로그 목록 */
  async getAuditLogs(params?: { limit?: number; userId?: string; action?: string; tenantId?: string }): Promise<AuditLog[]> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.userId) query.set('userId', params.userId);
    if (params?.action) query.set('action', params.action);
    if (params?.tenantId) query.set('tenantId', params.tenantId);
    const qs = query.toString() ? `?${query.toString()}` : '';
    const res = await apiClient.get<{ success: boolean; data: AuditLog[] }>(
      `/api/logs/audit${qs}`
    );
    return res.data.data;
  },

  /** 토큰 사용량 목록 */
  async getTokenUsage(params?: { limit?: number; tenantId?: string }): Promise<TokenUsageLog[]> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.tenantId) query.set('tenantId', params.tenantId);
    const qs = query.toString() ? `?${query.toString()}` : '';
    const res = await apiClient.get<{ success: boolean; data: TokenUsageLog[] }>(
      `/api/logs/token-usage${qs}`
    );
    return res.data.data;
  },

  /** 토큰 사용 기록 단건 삭제 (제품 관리자 전용) */
  async deleteTokenUsage(logId: string, userId: string): Promise<void> {
    await apiClient.delete(`/api/logs/token-usage/${encodeURIComponent(logId)}?userId=${encodeURIComponent(userId)}`);
  },
};

export default logsApi;
